import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText, Users } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-neutral-900">Privacy Policy</h1>
          <p className="text-neutral-600 mt-2">Last updated: January 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 space-y-8">
          {/* Introduction */}
          <section>
            <div className="flex items-center mb-4">
              <Shield className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Introduction</h2>
            </div>
            <p className="text-neutral-700 leading-relaxed">
              Finance BPA ("we," "our," or "us") is committed to protecting your privacy
              and ensuring the security of your financial information. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you use our
              financial planning and analysis platform.
            </p>
            <p className="text-neutral-700 leading-relaxed mt-4">
              By using our service, you agree to the collection and use of information in accordance
              with this policy. We take your privacy seriously, especially when it comes to
              financial data.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-center mb-4">
              <FileText className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Information We Collect</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Personal Information
                </h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Name, email address, and contact information</li>
                  <li>Company name and business details</li>
                  <li>Payment and billing information</li>
                  <li>Account credentials and authentication data</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Financial Data
                </h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Financial statements (Profit & Loss, Balance Sheet, Cash Flow)</li>
                  <li>Budget and forecast data</li>
                  <li>Transaction records and accounting data</li>
                  <li>Financial models and scenario analyses</li>
                  <li>Integration data from QuickBooks, Xero, NetSuite, and other systems</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Usage Data
                </h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>IP address and device information</li>
                  <li>Browser type and version</li>
                  <li>Pages visited and features used</li>
                  <li>Time and date of access</li>
                  <li>Error logs and performance metrics</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <div className="flex items-center mb-4">
              <Eye className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">How We Use Your Information</h2>
            </div>
            <ul className="list-disc list-inside text-neutral-700 space-y-2 ml-4">
              <li>To provide, maintain, and improve our financial planning and analysis services</li>
              <li>To process transactions and manage your account</li>
              <li>To generate financial reports, forecasts, and AI-powered insights</li>
              <li>To communicate with you about your account, updates, and support</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations and enforce our terms of service</li>
              <li>To analyze usage patterns and improve user experience</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-center mb-4">
              <Lock className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Data Security</h2>
            </div>
            <p className="text-neutral-700 leading-relaxed mb-4">
              We implement industry-standard security measures to protect your financial data:
            </p>
            <ul className="list-disc list-inside text-neutral-700 space-y-2 ml-4">
              <li>End-to-end encryption for data in transit (TLS/SSL)</li>
              <li>Encryption at rest for sensitive financial data</li>
              <li>Role-based access control and multi-factor authentication</li>
              <li>Regular security audits and penetration testing</li>
              <li>Secure data centers with physical access controls</li>
              <li>Compliance with SOC 2, GDPR, and financial data protection standards</li>
              <li>Regular backups and disaster recovery procedures</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <div className="flex items-center mb-4">
              <Users className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Data Sharing and Disclosure</h2>
            </div>
            <p className="text-neutral-700 leading-relaxed mb-4">
              We do not sell your financial data. We may share information only in the following
              circumstances:
            </p>
            <ul className="list-disc list-inside text-neutral-700 space-y-2 ml-4">
              <li>
                <strong>Service Providers:</strong> With trusted third-party vendors who assist in
                operating our platform (hosting, payment processing, analytics) under strict
                confidentiality agreements
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law, court order, or
                government regulation
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a merger, acquisition, or
                sale of assets, with notice to users
              </li>
              <li>
                <strong>With Your Consent:</strong> When you explicitly authorize us to share
                information
              </li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Your Privacy Rights</h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc list-inside text-neutral-700 space-y-2 ml-4">
              <li>
                <strong>Access:</strong> Request a copy of your personal and financial data
              </li>
              <li>
                <strong>Correction:</strong> Update or correct inaccurate information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your account and data (subject to
                legal retention requirements)
              </li>
              <li>
                <strong>Portability:</strong> Export your data in a machine-readable format
              </li>
              <li>
                <strong>Opt-Out:</strong> Unsubscribe from marketing communications
              </li>
              <li>
                <strong>Objection:</strong> Object to certain processing activities
              </li>
            </ul>
            <p className="text-neutral-700 leading-relaxed mt-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@kreupai.com" className="text-primary-600 hover:underline">
                privacy@kreupai.com
              </a>
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Data Retention</h2>
            <p className="text-neutral-700 leading-relaxed">
              We retain your financial data for as long as your account is active or as needed to
              provide services. After account termination, we may retain data for:
            </p>
            <ul className="list-disc list-inside text-neutral-700 space-y-2 ml-4 mt-4">
              <li>Legal compliance requirements (typically 7 years for financial records)</li>
              <li>Dispute resolution and enforcement of agreements</li>
              <li>Legitimate business purposes (with appropriate safeguards)</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Children's Privacy</h2>
            <p className="text-neutral-700 leading-relaxed">
              Our service is not intended for individuals under 18 years of age. We do not
              knowingly collect personal information from children. If you believe we have collected
              information from a child, please contact us immediately.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
              International Data Transfers
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than your own.
              We ensure appropriate safeguards are in place, including standard contractual clauses
              and adequacy decisions, to protect your data in accordance with this Privacy Policy.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
              Changes to This Privacy Policy
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new policy on this page and updating the "Last
              updated" date. Your continued use of the service after changes become effective
              constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-neutral-50 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Contact Us</h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact
              us:
            </p>
            <div className="space-y-2 text-neutral-700">
              <p>
                <strong>Email:</strong>{' '}
                <a href="mailto:privacy@kreupai.com" className="text-primary-600 hover:underline">
                  privacy@kreupai.com
                </a>
              </p>
              <p>
                <strong>Data Protection Officer:</strong>{' '}
                <a href="mailto:dpo@kreupai.com" className="text-primary-600 hover:underline">
                  dpo@kreupai.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

